import { useCallback, useContext, useMemo, useRef, useState, createContext, ReactNode } from "react"
import { RadioProps } from "trunx"
import cn from "classnames"

import { useDidMount } from "utils/customHooks"

interface Props extends RadioProps {
    isDisabled?: boolean
    showCheck?: boolean
}

function Radio({ className, isDisabled, showCheck = true, children, ...props }: Props) {
    const nodeRef = useRef<HTMLSpanElement>(null)
    const [isActive, setIsActive] = useState(false)
    const { register } = useRadioGroup()
    useDidMount(() => {
        if (nodeRef.current instanceof HTMLElement) {
            const input = nodeRef.current.parentElement?.querySelector("input")
            if (input) {
                return register(input, setIsActive)
            }
        }
    })
    return (
        <label
            className={cn(className, "radio b-radio blm-radio box", {
                "is-disabled": isDisabled,
                "is-active": isActive,
            })}
        >
            <input type="radio" disabled={isDisabled} {...props} />
            {showCheck && <span className="check" />}
            <span className="control-label">{children}</span>
            <span ref={nodeRef} style={{ display: "none" }} />
        </label>
    )
}

interface Handlers {
    register(this: void, node: HTMLInputElement, setActive: (value: boolean) => void): void
}

const Context = createContext<Handlers>(null!)

interface ProviderProps {
    children: ReactNode
}

interface NodeItem {
    eventsSource: HTMLInputElement
    setActive: (value: boolean) => void
}

export function RadioGroup({ children }: ProviderProps) {
    const nodes = useRef(new Set<NodeItem>())
    const onChange = useCallback((event: Event) => {
        if (event.target instanceof HTMLInputElement && event.target.checked) {
            nodes.current.forEach(({ eventsSource, setActive }) =>
                setActive(eventsSource === event.target)
            )
        }
    }, [])
    const handlers: Handlers = useMemo(
        () => ({
            register(node, setActive) {
                if (node.checked) setActive(true)
                node.addEventListener("change", onChange)
                nodes.current.add({ eventsSource: node, setActive })
                return () =>
                    nodes.current.forEach((item) => {
                        if (item.eventsSource === node) {
                            node.removeEventListener("change", onChange)
                            nodes.current.delete(item)
                        }
                    })
            },
        }),
        []
    )
    return <Context.Provider value={handlers}>{children}</Context.Provider>
}

RadioGroup.Item = Radio

function useRadioGroup() {
    return useContext(Context)
}
